import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../LangContext";
import api, { formatApiError } from "../api";
import { LOGO_URL } from "../constants";

export default function AdminLogin() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("rc_token", data.access_token);
      localStorage.setItem("rc_user", JSON.stringify(data.user));
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err, t.admin.loginError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF5] flex flex-col" data-testid="admin-login-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 w-full">
        <Link
          to="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-2 text-sm text-[#1e3a5f]/70 hover:text-[#7a8b50]"
        >
          <ArrowLeft className="h-4 w-4" /> {t.common.back}
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt="Ruizcare" className="h-20 w-20 object-contain mb-3" />
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#7a8b50] font-semibold">
              <ShieldCheck className="h-3.5 w-3.5" /> {t.admin.loginTitle}
            </div>
            <h1 className="mt-3 font-serif-display text-3xl text-[#1e3a5f] font-medium text-center">
              {t.admin.loginSubtitle}
            </h1>
          </div>

          <form
            onSubmit={submit}
            data-testid="admin-login-form"
            className="bg-white rounded-3xl p-8 border border-[#7a8b50]/20 shadow-sm space-y-5"
          >
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-[#7a8b50] font-semibold mb-2 block">
                {t.admin.email}
              </label>
              <input
                data-testid="admin-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[#1e3a5f]/20 rounded-xl px-4 py-3 text-sm text-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#7a8b50]/50"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-[#7a8b50] font-semibold mb-2 block">
                {t.admin.password}
              </label>
              <input
                data-testid="admin-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-[#1e3a5f]/20 rounded-xl px-4 py-3 text-sm text-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#7a8b50]/50"
              />
            </div>
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#1e3a5f] text-[#FAFAF5] font-semibold text-sm tracking-wide hover:bg-[#15293f] transition-all disabled:opacity-60"
            >
              {submitting ? "..." : t.admin.login}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
